import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { DISTRICTS, DISTRICT_TALUKA_MAP } from '../constants/locations';

function Forms() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [talukaFilter, setTalukaFilter] = useState('');
  const [ashaWorkerFilter, setAshaWorkerFilter] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState('all'); // 'all', 'hbnc', 'hbyc'
  const [loading, setLoading] = useState(true);
  const [talukaOptions, setTalukaOptions] = useState([]);
  const [ashaWorkers, setAshaWorkers] = useState([]);

  useEffect(() => {
    if (districtFilter) {
      setTalukaOptions(DISTRICT_TALUKA_MAP[districtFilter] || []);
    } else {
      setTalukaOptions([]);
      setTalukaFilter('');
    }
  }, [districtFilter]);

  useEffect(() => {
    fetchAshaWorkers();
  }, []);

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, districtFilter, talukaFilter, ashaWorkerFilter, formTypeFilter]);

  const fetchAshaWorkers = async () => {
    try {
      // Fetch ASHA workers using backend role filter
      let allWorkers = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await adminAPI.getUsers({
          skip,
          limit,
          role: 'ASHA_WORKER',
          is_active: true
        });
        const users = response.data.users || [];

        // Map to dropdown format
        const workers = users.map(u => ({
          label: u.full_name,
          value: u.id
        }));

        allWorkers = [...allWorkers, ...workers];

        // Check if there are more users to fetch
        hasMore = users.length === limit && response.data.total > skip + limit;
        skip += limit;
      }

      console.log('ASHA Workers loaded:', allWorkers);
      setAshaWorkers(allWorkers);
    } catch (err) {
      console.error('Failed to fetch ASHA workers:', err);
      console.error('Error details:', err.response);
    }
  };

  const fetchForms = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (districtFilter) params.district = districtFilter;
      if (talukaFilter) params.taluka = talukaFilter;

      let hbncForms = [];
      let hbycForms = [];

      if (formTypeFilter === 'all' || formTypeFilter === 'hbnc') {
        const hbncResponse = await adminAPI.getHBNCForms(params);
        hbncForms = hbncResponse.data.forms.map(form => ({ ...form, type: 'HBNC' }));
      }

      if (formTypeFilter === 'all' || formTypeFilter === 'hbyc') {
        const hbycResponse = await adminAPI.getHBYCForms(params);
        hbycForms = hbycResponse.data.forms.map(form => ({ ...form, type: 'HBYC' }));
      }

      let allForms = [...hbncForms, ...hbycForms].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      // Filter by ASHA worker if selected
      if (ashaWorkerFilter) {
        allForms = allForms.filter(form => form.userId === ashaWorkerFilter);
      }

      setForms(allForms);
      setTotal(allForms.length);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch forms:', err);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearch('');
    setDistrictFilter('');
    setTalukaFilter('');
    setAshaWorkerFilter('');
    setFormTypeFilter('all');
    setPage(0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('mr-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFormTypeColor = (type) => {
    return type === 'HBNC' ? '#E65100' : '#1976D2';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">फॉर्म</Typography>
        <IconButton onClick={handleReset} color="primary" title="फिल्टर रीसेट करा">
          <RefreshIcon />
        </IconButton>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <FormControl fullWidth size="small">
              <InputLabel>फॉर्म प्रकार</InputLabel>
              <Select
                value={formTypeFilter}
                label="फॉर्म प्रकार"
                onChange={(e) => setFormTypeFilter(e.target.value)}
              >
                <MenuItem value="all">सर्व</MenuItem>
                <MenuItem value="hbnc">HBNC</MenuItem>
                <MenuItem value="hbyc">HBYC</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              size="small"
              options={DISTRICTS}
              getOptionLabel={(option) => option.label}
              value={DISTRICTS.find(d => d.value === districtFilter) || null}
              onChange={(e, newValue) => setDistrictFilter(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="जिल्हा" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              size="small"
              options={talukaOptions}
              getOptionLabel={(option) => option.label}
              value={talukaOptions.find(t => t.value === talukaFilter) || null}
              onChange={(e, newValue) => setTalukaFilter(newValue?.value || '')}
              disabled={!districtFilter}
              renderInput={(params) => (
                <TextField {...params} label="तालुका" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Autocomplete
              size="small"
              options={ashaWorkers}
              getOptionLabel={(option) => option.label}
              value={ashaWorkers.find(w => w.value === ashaWorkerFilter) || null}
              onChange={(e, newValue) => setAshaWorkerFilter(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="आशा कार्यकर्ता" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <TextField
              fullWidth
              label="मोबाइल शोधा"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="मोबाइल नंबर"
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>प्रकार</TableCell>
                  <TableCell>आशा कार्यकर्ता</TableCell>
                  <TableCell>आईचे नाव</TableCell>
                  <TableCell>बालाचे नाव</TableCell>
                  <TableCell>जिल्हा</TableCell>
                  <TableCell>तालुका</TableCell>
                  <TableCell>मोबाइल नंबर</TableCell>
                  <TableCell>तारीख</TableCell>
                  <TableCell>स्थिती</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((form) => (
                  <TableRow
                    key={`${form.type}-${form.id}`}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/forms/${form.type.toLowerCase()}/${form.id}`)}
                  >
                    <TableCell>
                      <Chip
                        label={form.type}
                        size="small"
                        sx={{
                          backgroundColor: getFormTypeColor(form.type),
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>{form.ashaWorkerName}</TableCell>
                    <TableCell>
                      {form.type === 'HBNC'
                        ? form.formData?.motherNewborn?.motherName || '-'
                        : form.formData?.childProfile?.motherName || '-'}
                    </TableCell>
                    <TableCell>
                      {form.type === 'HBNC'
                        ? form.formData?.motherNewborn?.childName || '-'
                        : form.formData?.childProfile?.childName || '-'}
                    </TableCell>
                    <TableCell>
                      {form.type === 'HBNC'
                        ? DISTRICTS.find(d => d.value === form.formData?.identification?.district)?.label || '-'
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {form.type === 'HBNC'
                        ? form.formData?.identification?.taluka
                          ? DISTRICT_TALUKA_MAP[form.formData?.identification?.district]?.find(
                              t => t.value === form.formData?.identification?.taluka
                            )?.label || form.formData?.identification?.taluka
                          : '-'
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {form.type === 'HBNC'
                        ? form.formData?.motherNewborn?.mobileNumber || '-'
                        : form.formData?.childProfile?.motherMobile || '-'}
                    </TableCell>
                    <TableCell>{formatDate(form.createdAt)}</TableCell>
                    <TableCell>
                      <Chip
                        label={form.syncStatus === 'synced' ? 'समक्रमित' : 'प्रलंबित'}
                        color={form.syncStatus === 'synced' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="प्रति पृष्ठ पंक्ती:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} पैकी ${count}`}
            />
          </>
        )}
      </TableContainer>
    </Container>
  );
}

export default Forms;
